import React from 'react';
import { PayrollAdjustmentBookingsTable } from './PayrollAdjustmentBookingsTable';
import { PayrollAdjustmentBookingsToolbar } from './PayrollAdjustmentBookingsToolbar';
import { PayrollAdjustmentSummaryCards } from './PayrollAdjustmentSummaryCards';
import {
  computePayrollAdjustmentSummary,
  filterPayrollAdjustmentRows,
  sortPayrollAdjustmentRows,
  todayYmd,
  togglePayrollAdjustmentSort,
  type PayrollAdjustmentSortState,
  type PayrollAdjustmentTableSortColumn,
} from './payrollAdjustmentListShared';
import { usePayrollAdjustmentDrawer } from './usePayrollAdjustmentDrawer';
import type {
  PayrollAdjustmentCategory,
  PayrollAdjustmentEntryRow,
  PayrollAdjustmentMonthRow,
  PayrollAdjustmentRuleRow,
} from '../../../utils/payrollAdjustmentCategories';

type EmployeeProfileBeAbzuegeTabProps = {
  employeeId: string;
  employeeName: string;
};

const TABLE_COLUMNS: Array<{
  label: string;
  sortKey: PayrollAdjustmentTableSortColumn;
  align?: 'left' | 'right';
}> = [
  { label: 'Datum', sortKey: 'date' },
  { label: 'Kategorie', sortKey: 'category' },
  { label: 'Lohnart', sortKey: 'lohnart' },
  { label: 'Notiz', sortKey: 'note' },
  { label: 'Betrag', sortKey: 'amount', align: 'right' },
];

export function EmployeeProfileBeAbzuegeTab({
  employeeId,
  employeeName,
}: EmployeeProfileBeAbzuegeTabProps) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<PayrollAdjustmentCategory[]>([]);
  const [items, setItems] = React.useState<PayrollAdjustmentMonthRow[]>([]);
  const [entries, setEntries] = React.useState<PayrollAdjustmentEntryRow[]>([]);
  const [rules, setRules] = React.useState<PayrollAdjustmentRuleRow[]>([]);
  const [search, setSearch] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('__all');
  const [sort, setSort] = React.useState<PayrollAdjustmentSortState>({ key: 'date', dir: 'desc' });

  const categoryByKey = React.useMemo(
    () => new Map(categories.map((c) => [c.key, c])),
    [categories],
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/employees/${encodeURIComponent(employeeId)}/payroll-adjustments/history`,
        { credentials: 'include' },
      );
      const json = (await res.json()) as {
        items?: PayrollAdjustmentMonthRow[];
        categories?: PayrollAdjustmentCategory[];
        entries?: PayrollAdjustmentEntryRow[];
        rules?: PayrollAdjustmentRuleRow[];
        error?: string;
      };
      if (!res.ok) throw new Error(json.error || 'Historie konnte nicht geladen werden.');
      setItems(Array.isArray(json.items) ? json.items : []);
      setCategories(Array.isArray(json.categories) ? json.categories : []);
      setEntries(Array.isArray(json.entries) ? json.entries : []);
      setRules(Array.isArray(json.rules) ? json.rules : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const summary = React.useMemo(() => computePayrollAdjustmentSummary(items), [items]);

  const sortedRows = React.useMemo(() => {
    const filtered = filterPayrollAdjustmentRows(items, { search, typeFilter, categoryByKey });
    return sortPayrollAdjustmentRows(filtered, sort, categoryByKey, 'desc');
  }, [items, search, typeFilter, categoryByKey, sort]);

  const {
    openAdd,
    openEditEntry,
    openEditRule,
    deleteEntry,
    deleteRule,
    drawer,
  } = usePayrollAdjustmentDrawer({
    categories,
    entries,
    rules,
    categoryByKey,
    employees: [{ id: employeeId, name: employeeName }],
    defaultEmployeeId: employeeId,
    defaultFromYmd: todayYmd(),
    hideEmployeeField: true,
    onReload: load,
  });

  return (
    <div className="space-y-4">
      <PayrollAdjustmentSummaryCards {...summary} totalLabels />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <PayrollAdjustmentBookingsToolbar
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        onAdd={() => openAdd()}
        searchFullWidth
      />

      <PayrollAdjustmentBookingsTable
        rows={sortedRows}
        columns={TABLE_COLUMNS}
        sort={sort}
        onSort={(key) => setSort((prev) => togglePayrollAdjustmentSort(prev, key))}
        categoryByKey={categoryByKey}
        loading={loading}
        emptyMessage={`Noch keine Be- oder Abzüge für ${employeeName}. Lege oben eine neue Buchung an.`}
        deleteRecurringRows
        onEditEntry={openEditEntry}
        onEditRule={openEditRule}
        onDeleteEntry={(id) => void deleteEntry(id)}
        onDeleteRule={(id) => void deleteRule(id)}
      />

      {drawer}
    </div>
  );
}
